<template>
    <div class="container">
      <h1>Ottawood Invoice System</h1>
      <div class="desc-box">
        <p>Thank you for providing services to us. Please use our online system to generate your invoice and submit it to your Ottawood contact person so we can process your payment promptly.</p>
        <p><b>Note:</b> If you choose to issue the invoice with an HST number, the system will automatically calculate HST/GST at a rate of 13%. If certain items are not subject to HST or are subject to a different tax rate, you will need to adjust them manually.</p>
      </div>
      <!-- <div v-if="showWarningBar" class="custom-warning-bar">
        <span class="warning-bar-icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="10" fill="#FFD600"/>
            <text x="10" y="15" text-anchor="middle" font-size="16" font-weight="bold" fill="#fff">!</text>
          </svg>
        </span>
        <span class="warning-bar-text">Some information missing or incorrect</span>
      </div> -->
  
      <section>
        <h2>Invoice Information</h2>
        <div class="form-row">
          <label>Invoice No.:</label>
          <input type="text" v-model="form.invoiceNo" disabled />
        </div>
        <div class="form-row">
          <label><span class="required">*</span>Issue Date:</label>
          <div style="position:relative;display:inline-block;">
            <el-date-picker
              v-model="form.issueDate"
              type="date"
              placeholder="MM/DD/YYYY"
              format="MM/DD/YYYY"
              value-format="MM/DD/YYYY"
              style="width: 240px; height: 24px;"
              :class="{'input-error': errors.issueDate}"
              @blur="validateRequired('issueDate')"
              @change="clearError('issueDate')"
            />
            <span v-if="errors.issueDate" class="input-error-icon_time">!</span>
          </div>
        </div>
        <div class="form-row">
          <label><span class="required">*</span>Due Date:</label>
          <div style="position:relative;display:inline-block;">
            <el-date-picker
              v-model="form.dueDate"
              type="date"
              placeholder="MM/DD/YYYY"
              format="MM/DD/YYYY"
              value-format="MM/DD/YYYY"
              style="width: 240px; height: 24px;"
              :class="{'input-error': errors.dueDate || errors.dueDateEarly}"
              @blur="validateDueDate()"
              @change="validateDueDate()"
            />
            <span v-if="errors.dueDateEarly" class="input-error-icon_time">!</span>
            <span v-else-if="errors.dueDate" class="input-error-icon_time">!</span>
            <span v-if="errors.dueDateEarly" class="due-date-error-msg">The due date cannot be earlier than the issue date.</span>
          </div>
        </div>
        <div class="form-row">
          <label><span class="required">*</span> Film Code:</label>
          <div style="position:relative;display:inline-block;">
            <input
              type="text"
              v-model="form.filmCode"
              placeholder="e.g. w0001"
              :class="{'input-error': errors.filmCode}"
              @blur="validateRequired('filmCode')"
              @input="clearError('filmCode'); validateTextLength($event, 'filmCode', 10)"
              maxlength="10"
            />
            <span v-if="errors.filmCode" class="input-error-icon">!</span>
          </div>
        </div>
        <div class="form-row">
          <label><span class="required">*</span>Role:</label>
          <div style="position:relative;display:inline-block;">
            <input
              type="text"
              v-model="form.role"
              placeholder="e.g. Production Assistant"
              :class="{'input-error': errors.role}"
              @blur="validateRequired('role')"
              @input="clearError('role'); validateTextLength($event, 'role', 200)"
              maxlength="200"
            />
            <span v-if="errors.role" class="input-error-icon">!</span>
          </div>
        </div>
      </section>
  
      <div style="background-color: #E0E0E0;width: 100vw;height: 1px;"/>
  
      <section>
        <h2>Payable To</h2>
        <div class="form-row">
          <label><span class="required">*</span>Name:</label>
          <div style="position:relative;display:inline-block;">
            <input
              type="text"
              v-model="form.name"
              placeholder="Your name or company"
              :class="{'input-error': errors.name}"
              @blur="validateRequired('name')"
              @input="clearError('name'); validateTextLength($event, 'name', 200)"
              maxlength="200"
            />
            <span v-if="errors.name" class="input-error-icon">!</span>
          </div>
        </div>
        <div class="form-row">
          <label><span class="required">*</span>Address:</label>
          <div style="position:relative;display:inline-block;">
            <input
              type="text"
              v-model="form.address1"
              placeholder="Address Line1"
              :class="{'input-error': errors.address1}"
              @blur="validateRequired('address1')"
              @input="clearError('address1'); validateTextLength($event, 'address1', 200)"
              maxlength="200"
            />
            <span v-if="errors.address1" class="input-error-icon">!</span>
          </div>
        </div>
        <div class="form-row">
          <label></label>
          <div style="position:relative;display:inline-block;">
            <input 
              type="text" 
              v-model="form.address2" 
              placeholder="Address Line2 (Apt, Unit, etc.)" 
              @input="validateTextLength($event, 'address2', 200)"
              maxlength="200"
            />
          </div>
        </div>
        <div class="form-row">
          <label><span class="required">*</span>City:</label>
          <div style="position:relative;display:inline-block;">
            <input
              type="text"
              v-model="form.city"
              placeholder="City"
              :class="{'input-error': errors.city}"
              @blur="validateRequired('city')"
              @input="clearError('city'); validateTextLength($event, 'city', 200)"
              maxlength="200"
            />
            <span v-if="errors.city" class="input-error-icon">!</span>
          </div>
        </div>
        <div class="form-row">
          <label><span class="required">*</span>Province:</label>
          <div style="position:relative;display:inline-block;">
            <select
              v-model="form.province"
              :class="{'input-error': errors.province}"
              @blur="validateRequired('province')"
              @change="clearError('province')"
            >
              <option value="">select</option>
              <option value="ON">ON</option>
              <option value="QC">QC</option>
              <option value="BC">BC</option>
              <option value="AB">AB</option>
              <option value="MB">MB</option>
              <option value="NS">NS</option>
              <option value="PE">PE</option>
              <option value="SK">SK</option>
              <option value="YT">YT</option>
              <option value="NT">NT</option>
              <option value="NU">NU</option>
              <option value="YK">YK</option>
              <!-- 省份可补充 -->
            </select>
            <span v-if="errors.province" class="input-error-icon">!</span>
          </div>
        </div>
        <div class="form-row">
          <label><span class="required">*</span>Postal Code:</label>
          <div style="position:relative;display:inline-block;">
            <input
              type="text"
              v-model="form.postalCode"
              placeholder="Postal Code"
              :class="{'input-error': errors.postalCode}"
              @blur="validateRequired('postalCode')"
              @input="clearError('postalCode'); validateTextLength($event, 'postalCode', 200)"
              maxlength="200"
            />
            <span v-if="errors.postalCode" class="input-error-icon">!</span>
          </div>
        </div>
        <div class="form-row">
          <label><span class="required">*</span>Email:</label>
          <div style="position:relative;display:inline-block;">
            <input
              type="email"
              v-model="form.email"
              placeholder="email address"
              :class="{'input-error': errors.email || errors.emailFormat}"
              @blur="validateEmail('email')"
              @input="clearError('email'); errors.emailFormat=false;"
            />
            <span v-if="errors.email || errors.emailFormat" class="input-error-icon">!</span>
          </div>
          <span class="error" v-if="errors.email">Email is required.</span>
          <span class="error" v-else-if="errors.emailFormat">The email format is invalid.</span>
        </div>
                <div class="form-row">
          <label><span class="required">*</span>Tax ID:</label>
          <div style="position:relative;display:flex;align-items:center;">
            <select v-model="form.taxType" style="width: 76px; margin-right: 8px;">
              <option value="SIN">SIN #</option>
              <option value="HST">HST #</option>
              <option value="NR">NR</option>
            </select>
            <div style="display: flex; align-items: center;">
              <input
                v-model="form.taxId"
                :placeholder="form.taxType === 'SIN' ? '123-456-789' : form.taxType === 'HST' ? '123-456-789' : 'Non-resident of Canada'"
                :disabled="form.taxType === 'NR'"
                style="width: 180px;"
                :class="{'input-error': errors.taxId || errors.taxIdFormat}"
                @blur="validateRequired('taxId')"
                @input="clearError('taxId'); validateTaxId($event)"
                maxlength="11"
              />
            </div>
            <span v-if="errors.taxId || errors.taxIdFormat" class="input-error-icon">!</span>
          </div>
          <span v-if="form.taxType === 'HST'" style="margin-left: 4px; color: #666;">RT 0001</span>
          <span class="error" v-if="errors.taxIdFormat">The Tax ID should be 9 digits long.</span>
        </div>
      </section>
  
      <div style="background-color: #E0E0E0;width: 100vw;height: 1px;"/>
  
      <section>
        <h2>Invoice Details</h2>
        <table class="invoice-table">
          <thead>
            <tr>
              <th><span class="required">*</span>Description</th>
              <th><span class="required">*</span>Rate</th>
              <th><span class="required">*</span>QTY</th>
              <th><span class="required">*</span>Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in form.items" :key="idx">
              <td>
                <div style="position:relative;display:inline-block;width: 100%;" class="input-box">
                  <input type="text" v-model="item.desc" placeholder="Description of service" :class="{'input-error': errors.invoiceDetails && errors.invoiceDetails[idx] && errors.invoiceDetails[idx].desc}" @blur="validateInvoiceItemField('desc', idx)" @input="validateTextLength($event, 'desc', 200, idx)" maxlength="200" />
                  <span v-if="errors.invoiceDetails && errors.invoiceDetails[idx] && errors.invoiceDetails[idx].desc" class="input-error-icon">!</span>
                </div>
              </td>
              <td>
                <div style="position:relative;display:inline-block;width: 100%;" class="input-box">
                  <input
                    type="text"
                    :value="item.rate"
                    placeholder="Rate"
                    :class="{'input-error': errors.invoiceDetails && errors.invoiceDetails[idx] && errors.invoiceDetails[idx].rate}"
                    @input="validateRate($event, idx)"
                    @blur="formatRateOnBlur($event, idx); validateInvoiceItemField('rate', idx)"
                  />
                  <span v-if="errors.invoiceDetails && errors.invoiceDetails[idx] && errors.invoiceDetails[idx].rate" class="input-error-icon">!</span>
                </div>
              </td>
              <td>
                <div style="position:relative;display:inline-block;width: 100%;" class="input-box">
                  <input
                    type="text"
                    :value="item.qty"
                    placeholder="Quantity"
                    :class="{'input-error': errors.invoiceDetails && errors.invoiceDetails[idx] && errors.invoiceDetails[idx].qty}"
                    @input="validateQty($event, idx)"
                    @blur="validateInvoiceItemField('qty', idx)"
                  />
                  <span v-if="errors.invoiceDetails && errors.invoiceDetails[idx] && errors.invoiceDetails[idx].qty" class="input-error-icon">!</span>
                </div>
              </td>
              <td>
                <input type="number" :value="(item.rate * item.qty).toFixed(2)" readonly style="background:#f5f5f6;" />
              </td>
              <td style="display:flex;align-items:center;gap:8px;">
                <img
                  :src="idx === 0 ? require('@/assets/remove_dis.png') : require('@/assets/remove.png')"
                  :style="{cursor: idx === 0 ? 'not-allowed' : 'pointer', width: '20px', height: '20px'}"
                  @click="idx !== 0 && removeItem(idx)"
                  :alt="idx === 0 ? 'Remove Disabled' : 'Remove'"
                />
                <img
                  :src="idx === 4 ? require('@/assets/add_dis.png') : require('@/assets/add.png')"
                  :style="{cursor: idx === 4 ? 'not-allowed' : 'pointer', width: '20px', height: '20px'}"
                  @click="idx !== 4 && form.items.length < 5 && addItem()"
                  :alt="idx === 4 ? 'Add Disabled' : 'Add'"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <div class="summary">
          <div>Subtotal: ${{ subtotal.toFixed(2) }}</div>
          <div style="margin: 12px 0;">
            Tax (GST/HST):
            <input
              :value="form.taxType === 'SIN' ? 0 : taxAmount"
              type="number"
              min="0"
              step="0.01"
              :disabled="form.taxType === 'SIN'"
              style="width:120px;  text-align: right; margin: 0 4px;"
              @input="form.taxType !== 'SIN' && updateTaxAmount($event)"
            />
          </div>
          <div style="font-size: 16px;font-weight: bold;" class="bold">Amount Due: ${{ total.toFixed(2) }}</div>
        </div>
      </section>
  
      <div style="background-color: #E0E0E0;width: 100vw;height: 1px;"/>
  
      <section>
        <h2>BANK INFORMATION</h2>
        <div class="form-row">
          <label><span class="required">*</span>Institution No.:</label>
          <div style="position:relative;display:inline-block;">
            <input
              type="text"
              v-model="form.institutionNo"
              placeholder="3-digit number"
              :class="{'input-error': errors.institutionNo || errors.institutionNoFormat}"
              @blur="validateRequired('institutionNo')"
              @input="clearError('institutionNo'); validateInstitutionNo($event)"
              maxlength="3"
            />
            <span v-if="errors.institutionNo || errors.institutionNoFormat" class="input-error-icon">!</span>
          </div>
          <span class="error" v-if="errors.institutionNo">Must be a 3-digit number.</span>
          <span class="error" v-else-if="errors.institutionNoFormat">Institution No. must be exactly 3 digits.</span>
        </div>
        <div class="form-row">
          <label><span class="required">*</span>Transit No.:</label>
          <div style="position:relative;display:inline-block;">
            <input
              type="text"
              v-model="form.transitNo"
              placeholder="5-digit number"
              :class="{'input-error': errors.transitNo || errors.transitNoFormat}"
              @blur="validateRequired('transitNo')"
              @input="clearError('transitNo'); validateTransitNo($event)"
              maxlength="5"
            />
            <span v-if="errors.transitNo || errors.transitNoFormat" class="input-error-icon">!</span>
          </div>
          <span class="error" v-if="errors.transitNo">Must be a 5-digit number.</span>
          <span class="error" v-else-if="errors.transitNoFormat">Transit No. must be exactly 5 digits.</span>
        </div>
        <div class="form-row">
          <label><span class="required">*</span>Account No.:</label>
          <div style="position:relative;display:inline-block;">
            <input
              type="text"
              v-model="form.accountNo"
              placeholder="7 to 12-digit number"
              :class="{'input-error': errors.accountNo || errors.accountNoFormat}"
              @blur="validateRequired('accountNo')"
              @input="clearError('accountNo'); validateAccountNo($event)"
              maxlength="12"
            />
            <span v-if="errors.accountNo || errors.accountNoFormat" class="input-error-icon">!</span>
          </div>
          <span class="error" v-if="errors.accountNo">Must be a 7 to 12-digit number.</span>
          <span class="error" v-else-if="errors.accountNoFormat">Account No. must be 7-12 digits.</span>
        </div>
        <div class="form-row">
          <label><span class="required">*</span>EMT Email:</label>
          <div style="position:relative;display:inline-block;">
            <input
              type="email"
              v-model="form.emtEmail"
              placeholder="EMT Email"
              :class="{'input-error': errors.emtEmail || errors.emtEmailFormat}"
              @blur="validateRequired('emtEmail'); validateEmail('emtEmail')"
              @input="clearError('emtEmail'); errors.emtEmailFormat=false; validateTextLength($event, 'emtEmail', 200)"
              maxlength="200"
            />
            <span v-if="errors.emtEmail || errors.emtEmailFormat" class="input-error-icon">!</span>
          </div>
          <span class="error" v-if="errors.emtEmail">EMT Email is required.</span>
          <span class="error" v-else-if="errors.emtEmailFormat">The email format is invalid.</span>
        </div>

        <div class="note">
          <p>Please enable Autodeposit.<br />If a refund occurs due to Autodeposit not being set up and a bank fee is charged, the supplier will be responsible for the fee.</p>
        </div>
      </section>
  
      <footer>
        <button class="preview" @click="handlePreview">Preview</button>
        <button class="download" @click="handleDownload">Download</button>
      </footer>
    </div>
    <el-dialog v-model="showPreview" width="700px" top="2vh">
      <InvoicePreview
        ref="previewRef"
        :invoiceNo="form.invoiceNo"
        :issueDate="form.issueDate"
        :dueDate="form.dueDate"
        :filmCode="form.filmCode"
        :role="form.role"
        :name="form.name"
        :address1="form.address1"
        :address2="form.address2"
        :city="form.city"
        :province="form.province"
        :postalCode="form.postalCode"
        :email="form.email"
        :taxType="form.taxType"
        :taxId="form.taxId"
        :items="form.items"
        :subtotal="subtotal.toFixed(2)"
        :tax="tax.toFixed(2)"
        :total="total.toFixed(2)"
        :institutionNo="form.institutionNo"
        :transitNo="form.transitNo"
        :accountNo="form.accountNo"
        :emtEmail="form.emtEmail"
      />

    </el-dialog>
  </template>
  
  <script setup>
  import { h, reactive, computed, ref, watch, nextTick } from 'vue';
  import { ElDatePicker, ElMessage } from 'element-plus';
  import InvoicePreview from './InvoicePreview.vue';
  import html2pdf from 'html2pdf.js';
  import { generateInvoiceHtml } from '@/utils/htmlGenerator';
  
  function randomInvoiceNo() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const randomNum1 = Math.floor(Math.random() * 10);
    const randomNum2 = Math.floor(Math.random() * 10);
    const randomLetter = String.fromCharCode(65 + Math.floor(Math.random() * 26)); // A-Z
    const randomNum3 = Math.floor(Math.random() * 10);
    
    return `${year}${month}${day}${randomNum1}${randomNum2}${randomLetter}${randomNum3}`;
  }
  function validateQty(event, idx) {
    let value = event.target.value;
    
    // 只允许数字，移除所有非数字字符
    value = value.replace(/[^0-9]/g, '');
    
    // 限制最大为 3 位数
    if (value.length > 3) {
      value = value.slice(0, 3);
    }
    
    // 移除前导零
    value = value.replace(/^0+/, '');
    
    // 如果是空字符串，保持为空；否则转换为整数
    form.items[idx].qty = value === '' ? '' : value;
    
    // 更新输入框显示的值
    event.target.value = form.items[idx].qty;
  }

  function validateRate(event, idx) {
    let value = event.target.value;
    
    // 只允许数字和一个小数点
    value = value.replace(/[^0-9.]/g, '');
    
    // 只保留第一个小数点
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // 小数点前最多5位
    if (parts[0] && parts[0].length > 5) {
      value = parts[0].slice(0, 5) + (parts[1] ? '.' + parts[1] : '');
    }
    
    // 小数点后最多2位
    if (parts[1] && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].slice(0, 2);
    }
    
    // 直接更新数据
    form.items[idx].rate = value;
  }

  function formatRateOnBlur(event, idx) {
    let value = form.items[idx].rate;
    
    if (value === '') {
      return;
    }
    
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      form.items[idx].rate = numValue.toFixed(2);
    } else {
      form.items[idx].rate = '';
    }
  }
  function validateTaxId(event) {
    // 如果是 NR 类型，不需要验证
    if (form.taxType === 'NR') {
      errors.taxId = false;
      errors.taxIdFormat = false;
      form.taxId = 'Non-resident of Canada';
      return;
    }
    
    let value = event.target.value;
    
    // 移除任何 RT xxxx 后缀
    value = value.replace(/\s*RT\s*\d+/g, '').trim();
    
    // Remove any non-numeric characters
    value = value.replace(/[^0-9]/g, '');
    
    // Limit to 9 digits
    if (value.length > 9) {
      value = value.slice(0, 9);
    }
    
    // Format as XXX-XXX-XXX
    if (value.length >= 6) {
      value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6);
    } else if (value.length >= 3) {
      value = value.slice(0, 3) + '-' + value.slice(3);
    }
    
    form.taxId = value;
    event.target.value = value;
    
    // Validate if it's exactly 9 digits
    const numericValue = value.replace(/[^0-9]/g, '');
    if (numericValue.length > 0 && numericValue.length !== 9) {
      errors.taxIdFormat = true;
    } else {
      errors.taxIdFormat = false;
    }
  }
  
  function validateInstitutionNo(event) {
    let value = event.target.value;
    
    // Remove any non-numeric characters
    value = value.replace(/[^0-9]/g, '');
    
    // Limit to 3 digits
    if (value.length > 3) {
      value = value.slice(0, 3);
    }
    
    form.institutionNo = value;
    event.target.value = value;
    
    // Validate if it's exactly 3 digits
    if (value.length > 0 && value.length !== 3) {
      errors.institutionNoFormat = true;
    } else {
      errors.institutionNoFormat = false;
    }
  }
  
  function validateTransitNo(event) {
    let value = event.target.value;
    
    // Remove any non-numeric characters
    value = value.replace(/[^0-9]/g, '');
    
    // Limit to 5 digits
    if (value.length > 5) {
      value = value.slice(0, 5);
    }
    
    form.transitNo = value;
    event.target.value = value;
    
    // Validate if it's exactly 5 digits
    if (value.length > 0 && value.length !== 5) {
      errors.transitNoFormat = true;
    } else {
      errors.transitNoFormat = false;
    }
  }
  
  function validateTextLength(event, field, maxLength = 200, idx = null) {
    const value = event.target.value;
    if (value.length > maxLength) {
      if (idx !== null) {
        // For invoice items
        form.items[idx].desc = value.slice(0, maxLength);
        event.target.value = form.items[idx].desc;
      } else {
        // For regular form fields
        form[field] = value.slice(0, maxLength);
        event.target.value = form[field];
      }
    }
  }
  
  const form = reactive({
    invoiceNo: randomInvoiceNo(),
    issueDate: '',
    dueDate: '',
    filmCode: '',
    role: '',
    name: '',
    address1: '',
    address2: '',
    city: '',
    province: 'ON',
    postalCode: '',
    email: '',
    taxType: 'SIN',
    taxId: '',
    items: [
      { desc: '', rate: '', qty: '' },
    ],
    institutionNo: '',
    transitNo: '',
    accountNo: '',
    emtEmail: '',
  });
  
  const errors = reactive({
    dueDate: false,
    dueDateEarly: false,
    email: false,
    institutionNo: false,
    transitNo: false,
    accountNo: false,
    name: false,
    address1: false,
    city: false,
    province: false,
    postalCode: false,
    emailFormat: false,
    institutionNoFormat: false,
    transitNoFormat: false,
    accountNoFormat: false,
    issueDate: false,
    filmCode: false,
    role: false,
    taxId: false,
    taxIdFormat: false,
    emtEmail: false,
    emtEmailFormat: false,
    invoiceDetails: [],
  });
  
  const taxRate = ref(13);
  const taxAmount = ref(0);
  const subtotal = computed(() => form.items.reduce((sum, i) => sum + (Number(i.rate) * Number(i.qty)), 0));

  // 监听 subtotal 变化，自动更新 tax
  watch(subtotal, (newSubtotal) => {
    if (form.taxType === 'HST') {
      taxAmount.value = parseFloat((newSubtotal * taxRate.value / 100).toFixed(2));
    }
  });

  // 监听 taxType 变化
  watch(() => form.taxType, (newType) => {
    if (newType === 'SIN' || newType === 'NR') {
      taxAmount.value = 0;
      // 清除校验错误
      errors.taxId = false;
      errors.taxIdFormat = false;
      
      if (newType === 'NR') {
        form.taxId = 'Non-resident of Canada';
      } else {
        form.taxId = ''; // 切换到 SIN 时清空
      }
    } else if (newType === 'HST') {
      taxAmount.value = parseFloat((subtotal.value * taxRate.value / 100).toFixed(2));
      form.taxId = ''; // 切换到 HST 时清空
      // 清除校验错误
      errors.taxId = false;
      errors.taxIdFormat = false;
    }
  });

  const tax = computed(() => {
    if (form.taxType === 'SIN' || form.taxType === 'NR') {
      return 0;
    }
    return taxAmount.value;
  });

  const total = computed(() => {
    return parseFloat((subtotal.value + tax.value).toFixed(2));
  });
  
  const showWarningBar = ref(false);
  const showPreview = ref(false);
  const previewRef = ref(null);
  
  function addItem() {
    if (form.items.length < 5) {
      form.items.push({ desc: '', rate: '', qty: '' });
    }
  }
  function removeItem(idx) {
    if (form.items.length > 1 && idx !== 0) {
      form.items.splice(idx, 1);
    }
  }
  function validateRequired(field) {
    if (!form[field] || form[field].toString().trim() === '') {
      errors[field] = true;
    } else {
      errors[field] = false;
    }
  }
  function clearError(field) {
    errors[field] = false;
  }
  function validateDueDate() {
    if (!form.dueDate) {
      errors.dueDate = true;
      errors.dueDateEarly = false;
      return;
    }
    if (form.issueDate && form.dueDate < form.issueDate) {
      errors.dueDateEarly = true;
      errors.dueDate = false;
    } else {
      errors.dueDateEarly = false;
      errors.dueDate = false;
    }
  }
  function validateEmail(field) {
    const value = form[field];
    if (!value || value.toString().trim() === '') {
      errors[field] = true;
      errors[`${field}Format`] = false;
      return;
    }
    errors[field] = false;
    // 简单邮箱正则
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    errors[`${field}Format`] = !emailRegex.test(value);
  }
  function validateInvoiceItemField(field, idx) {
    if (!errors.invoiceDetails[idx]) {
      errors.invoiceDetails[idx] = {desc: false, rate: false, qty: false};
    }
    
    const item = form.items[idx];
    let isValid = true;
    
    if (field === 'desc') {
      isValid = item.desc && item.desc.toString().trim() !== '';
    } else if (field === 'rate') {
      isValid = item.rate !== null && item.rate !== undefined && item.rate !== '' && !isNaN(item.rate);
    } else if (field === 'qty') {
      isValid = item.qty !== null && item.qty !== undefined && item.qty !== '' && !isNaN(item.qty);
    }
    
    errors.invoiceDetails[idx][field] = !isValid;
  }
  function validateAllRequired() {
    let valid = true;
    // Invoice Information
    if (!form.issueDate) { errors.issueDate = true; valid = false; }
    if (!form.dueDate) { errors.dueDate = true; valid = false; }
    if (!form.filmCode) { errors.filmCode = true; valid = false; }
    if (!form.role) { errors.role = true; valid = false; }
    // Payable To
    if (!form.name) { errors.name = true; valid = false; }
    if (!form.address1) { errors.address1 = true; valid = false; }
    if (!form.city) { errors.city = true; valid = false; }
    if (!form.province) { errors.province = true; valid = false; }
    if (!form.postalCode) { errors.postalCode = true; valid = false; }
    if (!form.email) { errors.email = true; valid = false; }
    else if (form.email && errors.emailFormat) { valid = false; }
    if (!form.taxId && form.taxType !== 'NR') { errors.taxId = true; valid = false; }
    // Bank Info
    if (!form.institutionNo) { errors.institutionNo = true; valid = false; }
    if (!form.transitNo) { errors.transitNo = true; valid = false; }
    if (!form.accountNo) { errors.accountNo = true; valid = false; }
    else if (errors.accountNoFormat) { valid = false; }
    if (!form.emtEmail) { errors.emtEmail = true; valid = false; }
    else if (form.emtEmail && errors.emtEmailFormat) { valid = false; }
    // Invoice Details
    if (!errors.invoiceDetails) errors.invoiceDetails = [];
    form.items.forEach((item, idx) => {
      if (!errors.invoiceDetails[idx]) errors.invoiceDetails[idx] = {desc: false, rate: false, qty: false};
      if (!item.desc || item.desc.toString().trim() === '') { errors.invoiceDetails[idx].desc = true; valid = false; }
      else { errors.invoiceDetails[idx].desc = false; }
      if (item.rate === null || item.rate === undefined || item.rate === '' || isNaN(item.rate)) { errors.invoiceDetails[idx].rate = true; valid = false; }
      else { errors.invoiceDetails[idx].rate = false; }
      if (item.qty === null || item.qty === undefined || item.qty === '' || isNaN(item.qty)) { errors.invoiceDetails[idx].qty = true; valid = false; }
      else { errors.invoiceDetails[idx].qty = false; }
    });
    showWarningBar.value = !valid;
    return valid;
  }

  function handlePreview() {
    if (!validateAllRequired()) {
      ElMessage.warning('Some information missing or incorrect');
      return;
    }
    showPreview.value = true;
  }

  function handleDownload() {
    if (!validateAllRequired()) {
      ElMessage.warning('Some information missing or incorrect');
      return;
    }else{
      handleDownloadHtml();
    }
    // 下载逻辑
  }

  async function handleDownloadHtml() {
    if (!validateAllRequired()) {
      ElMessage.warning('Some information missing or incorrect');
      return;
    }

    try {
      const html = await generateInvoiceHtml(form, subtotal.value, tax.value, total.value);
      
      const formData = new FormData();
      formData.append('html', html);
      const response = await fetch('https://ufg.back.escroud.com/print_pdf', {
        method: 'POST',
        body: formData
      });
      const result = await response.json();
      if (result.code === 200 && result.data && result.data.file_url) {
        window.open(result.data.file_url, '_blank');
      } else {
        ElMessage.error(result.msg || 'PDF生成失败');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      ElMessage.error('PDF生成失败');
    }
  }

  function validateAccountNo(event) {
    let value = event.target.value.replace(/[^0-9]/g, '');
    if (value.length > 12) value = value.slice(0, 12);
    form.accountNo = value;
    event.target.value = value;
    // 校验7-12位
    if (value.length > 0 && (value.length < 7 || value.length > 12)) {
      errors.accountNoFormat = true;
    } else {
      errors.accountNoFormat = false;
    }
  }

  function updateTaxAmount(event) {
    const newValue = parseFloat(event.target.value);
    if (!isNaN(newValue) && newValue >= 0) {
      taxAmount.value = parseFloat(newValue.toFixed(2));
    } else {
      taxAmount.value = 0;
    }
  }
  </script>
  
  <style scoped>
  .input-box {
    position: relative;
  }
  .input-box .input-error-icon {
    top: 50% !important;
    right: 8px !important;
  }
  .container {
    margin: 0 auto;
    padding: 32px 24px 80px;
    background: #fff;
  }
  h1 {
    font-size: 2rem;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  .desc-box {
    background: #f5f5f6;
    border-radius: 4px;
    padding: 16px;
    margin-bottom: 24px;
    color: #333;
    width: 70%;
  }
  section { margin-bottom: 32px; }
  h2 { font-size: 1.1rem; margin: 24px 0 12px 0; font-weight: bold; }
  .form-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 12px;
  }
  .form-row label {
    min-width: 120px;
    font-weight: 500;
    margin-right: 8px;
    text-align: right;
  }
  .form-row input, .form-row select {
    margin-right: 8px;
    margin-bottom: 4px;
    padding: 4px 8px;
    border: 1px solid #E0E0E0;
    border-radius: 3px;
    width: 240px;
    height: 24px;
    transition: border-color 0.2s;
  }
  .form-row input[type="date"] {
    color: transparent;
  }
  .form-row input[type="date"]:focus {
    color: #333;
  }
  .form-row input[type="date"]:valid {
    color: #333;
  }
  .required { color: #e53935; margin-left: 2px; }
  .error { color: #e53935; font-size: 0.95em; margin-left: 8px; }
  .invoice-table {
    width: 65%;
    border-collapse: collapse;
    margin-bottom: 12px;
  }
  .invoice-table th, .invoice-table td {
    padding: 8px;
    text-align: left;
  }
  .invoice-table th {
    /* background: #f5f5f5; */
  }
  .invoice-table tbody tr:nth-child(even) {
    /* background: #f5f5f6; */
  }
  .invoice-table input {
    width: 240px;
    height: 24px;
    padding: 4px 8px;
    border: 1px solid #ccc;
    border-radius: 3px;
  }
  .summary {
    margin-top: 12px;
    text-align: right;
    width: 60%;
  }
  .bold { font-weight: bold; }
  .info-box {
    background: #fffbe6;
    border: 1px solid #ffe58f;
    color: #ad8b00;
    padding: 8px 16px;
    border-radius: 4px;
    margin: 16px 0;
    display: flex;
    align-items: center;
    max-width: 350px;
  }
  .note {
    color: #666;
    font-size: 0.95em;
    margin-top: 12px;
  }
  footer {
    position: fixed;
    left: 0; right: 0; bottom: 0;
    background: #222;
    padding: 24px 32px;
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    z-index: 10;
  }
  footer button {
    min-width: 120px;
    padding: 10px 0;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
  }
  .preview {
    background: #333333;
    color: #FFCC32;
    border: 1px solid #FFCC32;
  }
  .download {
    background: #ffd600;
    color: #222;
  }
  .el-date-picker, .el-input__wrapper, .el-input__inner {
    width: 240px !important;
    height: 24px !important;
    min-height: 24px !important;
    box-sizing: border-box;
    font-size: 1rem;
  }
  .el-input__prefix, .el-input__suffix {
    height: 24px !important;
    display: flex;
    align-items: center;
  }
  :deep(.el-date-editor .el-input__icon) {
    font-size: 16px !important;
    height: 20px !important;
    width: 20px !important;
    line-height: 20px !important;
  }
  :deep(.el-date-editor.input-error .el-input__wrapper) {
    box-shadow: 0 0 0 1px #ee0f0f inset !important;
  }
  :deep(.el-date-editor.input-error .el-input__inner) {
    border-color: #E95646 !important;
  }
  :deep(.el-date-editor.input-error) {
    border-color: #E95646 !important;
  }
  :deep(.el-date-editor.input-error .el-input) {
    border-color: #E95646 !important;
  }
  .custom-datepicker-popper .el-picker-panel {
    font-size: 1rem;
  }
  .input-error {
    border-color: #E95646 !important;
  }
  .input-error-icon_time {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;

    height: 16px;
    background: #E95646;
    border-radius: 50%;
    color: #fff;
    font-weight: bold;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    box-shadow: 0 0 0 2px #fff;
  }
  .input-error-icon {
    position: absolute;
    right: 12px;
    top: 40%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    background: #E95646;
    border-radius: 50%;
    color: #fff;
    font-weight: bold;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    box-shadow: 0 0 0 2px #fff;
  }
  .invoice-table td:nth-child(1) input {
    width: 100% !important;
  }
  .invoice-table td:nth-child(2) input,
  .invoice-table td:nth-child(3) input,
  .invoice-table td:nth-child(4) input {
    width: 100% !important;
  }
  .due-date-error-msg {
    position: absolute;
    left: 260px;
    top: 50%;
    transform: translateY(-50%);
    color: #E95646;
    font-size: 14px;
    white-space: nowrap;
    margin-left: 8px;
  }
  .custom-warning-message {
    padding: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }
  :deep(.custom-warning-bar) {
    display: flex;
    align-items: center;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px 0 rgba(0,0,0,0.06);
    padding: 12px 24px;
    font-size: 16px;
    color: #434A54;
    min-width: 340px;
    min-height: 48px;
    box-sizing: border-box;
  }
  :deep(.custom-warning-bar)::before {
    content: '';
    display: block;
    width: 4px;
    height: 40px;
    background: #FFD600;
    border-radius: 2px;
    margin-right: 20px;
  }
  :deep(.warning-bar-icon) {
    display: flex;
    align-items: center;
    margin-right: 24px;
    min-width: 20px;
  }
  :deep(.warning-bar-text) {
    color: #434A54;
    font-size: 16px;
    font-weight: 500;
    margin-left: 4px;
  }
  .form-row input[disabled] {
    background: #f5f5f6;
    color: #aaa;
  }
  
  /* Placeholder color */
  ::placeholder {
    color: #cccccc !important;
  }
  
  :-ms-input-placeholder {
    color: #cccccc !important;
  }
  
  ::-ms-input-placeholder {
    color: #cccccc !important;
  }
  
  /* Element Plus placeholder color */
  :deep(.el-input__inner::placeholder) {
    color: #cccccc !important;
  }
  
  :deep(.el-date-editor .el-input__inner::placeholder) {
    color: #cccccc !important;
  }
  </style>